import L from "leaflet";

export function enableProximityHover(
  map: L.Map,
  options: { tolerance?: number } = {}
): () => void {
  const tolerance = options.tolerance ?? 15;
  let currentHoveredLayer: L.Polyline | null = null;
  let polylines: L.Polyline[] = [];
  let collectTimer: ReturnType<typeof setTimeout> | null = null;

  function collectPolylines(): void {
    polylines = [];
    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON) {
        layer.eachLayer((sub) => {
          if (sub instanceof L.Polyline && !(sub instanceof L.Polygon))
            polylines.push(sub as L.Polyline);
        });
      } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        polylines.push(layer);
      }
    });
  }

  function scheduleCollect(): void {
    if (collectTimer !== null) clearTimeout(collectTimer);
    collectTimer = setTimeout(() => {
      collectTimer = null;
      collectPolylines();
    }, 150);
  }

  function getFlattenedPoints(polyline: L.Polyline): L.Point[] {
    const points: L.Point[] = [];
    function flatten(arr: any): void {
      if (!Array.isArray(arr)) {
        points.push(map.latLngToLayerPoint(arr as L.LatLng));
        return;
      }
      arr.forEach((item) => flatten(item));
    }
    flatten(polyline.getLatLngs());
    return points;
  }

  function getMinDistanceToPolyline(
    mousePoint: L.Point,
    polyline: L.Polyline
  ): number {
    const points = getFlattenedPoints(polyline);
    let minDist = Infinity;
    for (let i = 0; i < points.length - 1; i++) {
      const dist = L.LineUtil.pointToSegmentDistance(
        mousePoint,
        points[i],
        points[i + 1]
      );
      if (dist < minDist) minDist = dist;
    }
    return minDist;
  }

  function isOverMarkerIcon(e: L.LeafletMouseEvent): boolean {
    const target = e.originalEvent?.target as Element | null;
    return !!target?.closest(".leaflet-marker-icon");
  }

  function onMouseMove(e: L.LeafletMouseEvent): void {
    // Don't trigger proximity hover when mouse is directly over a marker icon
    if (isOverMarkerIcon(e)) {
      if (currentHoveredLayer) {
        currentHoveredLayer.fire("mouseout", {
          latlng: e.latlng,
          layerPoint: map.latLngToLayerPoint(e.latlng),
          containerPoint: e.containerPoint,
          originalEvent: e.originalEvent,
        });
        currentHoveredLayer = null;
      }
      return;
    }

    const mousePoint = map.latLngToLayerPoint(e.latlng);
    let closestPolyline: L.Polyline | null = null;
    let minDistance = Infinity;

    for (const polyline of polylines) {
      if (!map.hasLayer(polyline)) continue;
      const distance = getMinDistanceToPolyline(mousePoint, polyline);
      if (distance < minDistance) {
        minDistance = distance;
        closestPolyline = polyline;
      }
    }

    if (minDistance <= tolerance && closestPolyline !== null) {
      if (closestPolyline !== currentHoveredLayer) {
        if (currentHoveredLayer) {
          currentHoveredLayer.fire("mouseout", {
            latlng: e.latlng,
            layerPoint: mousePoint,
            containerPoint: e.containerPoint,
            originalEvent: e.originalEvent,
          });
        }
        (closestPolyline as L.Polyline).fire("mouseover", {
          latlng: e.latlng,
          layerPoint: mousePoint,
          containerPoint: e.containerPoint,
          originalEvent: e.originalEvent,
        });
        currentHoveredLayer = closestPolyline;
      } else {
        (closestPolyline as L.Polyline).fire("mousemove", {
          latlng: e.latlng,
          layerPoint: mousePoint,
          containerPoint: e.containerPoint,
          originalEvent: e.originalEvent,
        });
      }
    } else if (currentHoveredLayer) {
      currentHoveredLayer.fire("mouseout", {
        latlng: e.latlng,
        layerPoint: mousePoint,
        containerPoint: e.containerPoint,
        originalEvent: e.originalEvent,
      });
      currentHoveredLayer = null;
    }
  }

  function onMapMouseOut(): void {
    if (currentHoveredLayer) {
      currentHoveredLayer.fire("mouseout");
      currentHoveredLayer = null;
    }
  }

  collectPolylines();
  map.on("layeradd", scheduleCollect);
  map.on("layerremove", scheduleCollect);
  map.on("mousemove", onMouseMove);
  map.on("mouseout", onMapMouseOut);

  return function cleanup(): void {
    if (collectTimer !== null) clearTimeout(collectTimer);
    map.off("layeradd", scheduleCollect);
    map.off("layerremove", scheduleCollect);
    map.off("mousemove", onMouseMove);
    map.off("mouseout", onMapMouseOut);
    currentHoveredLayer = null;
    polylines = [];
  };
}
